import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Table, Typography, Spin, Segmented, Modal, Space, Button, Select, Descriptions, Tooltip } from "antd";
import { InfoCircleOutlined, PlusOutlined, FolderAddOutlined, EditOutlined, DeleteOutlined, WarningOutlined } from "@ant-design/icons";
import ExpandCollapseAllButton from "../../common/ExpandCollapseAllButton";
import NumberInput from "../../common/NumberInput";
import BcCalculationDetails, { computeBcCalculation, SummaryKeyRawFields } from "../../common/BcCalculationDetails";

import { useTender } from "../../../contexts/TenderContext";
import { useDevMode } from "@/contexts/DevModeContext";
import { useCanConfigureBoq } from "@/hooks/useCanConfigureBoq";
import { COLORS, formatUnit, MAX_SECTION_NESTING_DEPTH, MAX_SECTION_NESTING_DEPTH_MESSAGE, buildSectionDepthMap } from "../../../utils/constants";
import { useItemLibrary } from "../../../hooks/useItemLibrary";
import { useGlobalSettings } from "../../../hooks/useGlobalSettings";
import { useGetGeoToberWallBuildUp } from "../../../hooks/useWallSectionBuildUp";
import {
  geoToberWallSpecifications,
  geoToberWallFieldToSpecId,
} from "../../../mock/geo-tober-wall-build-up";
import { getNumericBoqVersion } from "../../../utils/boqVersionPrefix";
import { getGeoToberWallConfigureHeadersViolationsByField, type FieldConfigureHeadersViolation } from "../../../utils/groundworksConfigureHeadersValidation";
import { computeGeoToberWallSpecAmounts, type WallBuildUpDimensionItem } from "../../../utils/wallBuildUpCollectiveAmounts";
import { useBoqLatestVersion } from "../../../hooks/useBillOfQuantities";
import { BoqSummaryLineItem } from "../../../utils/groundworksBoqSummaryComputer";
import { computeDynamicBcCalculation, type DynamicBillSummaryLineItem } from '../../../utils/dynamicBillSummarySerialization';
import { useBillSummaryExtraItems, buildRootExtraRows, buildNestedExtraRows } from '../../../hooks/useBillSummaryExtraItems';
import { generatePlotMatrixCategoryColumns } from '../../../utils/plotMatrixCategoryGenerator';
import type { DynamicBillSummaryNode, DynamicBillSummaryItemNode, DynamicBillSummarySectionNode, DynamicBillSummaryResourceCodeEntry } from '../../../types/tender.types';

const findExtraNode = (nodes: DynamicBillSummaryNode[], id: string): DynamicBillSummaryNode | null => {
  for (const node of nodes) {
    if (node.id === id) return node;
    if (node.type === "section") {
      const found = findExtraNode(node.children, id);
      if (found) return found;
    }
  }
  return null;
};

const { Text, Title } = Typography;

// Header/section rowTypes across this table — 'sub-header' is the only one
// the FIXED data ever produces; 'main-header'/'column-type-header' only ever
// come from ad-hoc extra Section rows (root-level / nested respectively —
// see useBillSummaryExtraItems.tsx's buildExtraRow depth convention), but
// every column below still has to treat all three the same way so a
// newly-added extra section renders consistently instead of falling through
// to leaf-row rendering. Extra items are only ever wired into the Collective
// view (see collectiveDisplayData below) — Individual is a dev-mode-only
// per-spec breakdown with its own numbering scheme, not an attachment surface.
const isHeaderRowType = (rowType: string) =>
  rowType === "sub-header" || rowType === "main-header" || rowType === "column-type-header";

type BillMode = "collective" | "individual";

interface BillRow {
  key: string;
  category: string;
  description: string;
  quantity: string;
  unit: string;
  rate: string;
  amount: string;
  rowType: "main-header" | "sub-header" | "description-header" | "description" | "line-item" | "column-type-header";
  isDiameterHeader: boolean;
  children?: BillRow[];
  sectionAmount?: number;
  categoryName?: string;
  evaluateCode?: string;
  dbRate?: string;
  apiSummaryItem?: BoqSummaryLineItem | null;
  // Ad-hoc extra items (useBillSummaryExtraItems.tsx) — only present on rows
  // built by buildRootExtraRows/buildNestedExtraRows, merged into the
  // Collective view only.
  isExtra?: true;
  attachKey?: string;
  nodeType?: "section" | "item";
  amountType?: "number" | "text";
  amountText?: string;
  resourceCode?: string;
  resourceCodes?: DynamicBillSummaryResourceCodeEntry[];
  configViolation?: FieldConfigureHeadersViolation | null;
}

// Inverted map: specId -> fieldName
const specIdToField: Record<string, string> = {};
Object.entries(geoToberWallFieldToSpecId).forEach(([field, specId]) => {
  specIdToField[specId] = field;
});

const GEOToberWallBillSummary: React.FC = () => {
  const showBillModeToggle = useDevMode();
  const { state, updateField } = useTender();
  const { settings } = useGlobalSettings();
  const { data: itemLibraryData, isLoading: isLoadingItemLibrary } =
    useItemLibrary(state.region || "");
  const [billMode, setBillMode] = useState<BillMode>("collective");
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);

  const tenderName = state.projectName || "";
  const boqVersionNumber = useRef<number>((() => {
    const raw = localStorage.getItem("currentBoqVersion");
    return raw ? getNumericBoqVersion(JSON.parse(raw)?.version ?? 0) : 0;
  })()).current;
  // Always refetch from API when this bill summary mounts. The take-off page
  // (`GeoToberWall.tsx`) saves via `saveGeoToberWallBuildUp` — a direct API
  // POST that does NOT invalidate the React Query cache — so without
  // `refetchOnMount: "always"` (and `staleTime: 0`) the Groundworks Review
  // tab would serve stale cached specs and the bill summary totals would
  // diverge from the latest take-off / build-up data.
  const { data: persistedBuildUp } = useGetGeoToberWallBuildUp(tenderName, boqVersionNumber, {
    enabled: !!tenderName,
    staleTime: 0,
    refetchOnMount: "always",
  });

  const tpcwColumnHeaders: Record<string, string> =
    persistedBuildUp?.tobermoreColumnHeaders ||
    (state.groundworks as any)?.geoToberWallColumnHeaders ||
    {};
  const tpcwDimensionValues: Record<string, string> =
    persistedBuildUp?.tobermoreColumnDimensions ||
    (state.groundworks as any)?.geoToberWallDimensionValues ||
    {};

  const persistedSpecNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    persistedBuildUp?.specifications?.forEach((spec) => {
      map[spec.id] = spec.name;
    });
    return map;
  }, [persistedBuildUp]);

  const getTpcwLabel = (specId: string, fallback: string): string => {
    const match = specId.match(/^tobermore_(tpcw\d+)$/);
    if (!match) return fallback;
    const key = match[1];
    if (tpcwColumnHeaders[key]) return tpcwColumnHeaders[key];
    if (tpcwDimensionValues[key]) return `TPCW ${tpcwDimensionValues[key]}mm`;
    if (persistedSpecNameMap[specId] && persistedSpecNameMap[specId] !== fallback) return persistedSpecNameMap[specId];
    return fallback;
  };

  const itemLibraryRateMap = useMemo(() => {
    const map = new Map<string, number>();
    const items = itemLibraryData as unknown as any[];
    if (!Array.isArray(items)) return map;
    items.forEach((lib) => {
      const code = lib.resourceCode?.trim().toUpperCase();
      if (code) map.set(code, lib.nettRate ?? 0);
    });
    return map;
  }, [itemLibraryData]);

  const getRateFromLibrary = useCallback(
    (evaluateCode: string): number =>
      itemLibraryRateMap.get(evaluateCode.trim().toUpperCase()) ?? 0,
    [itemLibraryRateMap],
  );

  const [summaryKeyModalRecord, setSummaryKeyModalRecord] = useState<BoqSummaryLineItem | null>(null);
  const [configViolationModalRecord, setConfigViolationModalRecord] = useState<FieldConfigureHeadersViolation | null>(null);
  const configViolationsByField = useMemo(
    () => getGeoToberWallConfigureHeadersViolationsByField(state.groundworks),
    [state.groundworks],
  );
  // SummaryKey modal for ad-hoc extra items — these have no persisted boq-versions
  // entry (`apiSummaryItem`) since they aren't serialised into the exported BOQ
  // payload yet, so this shows the live-derived value instead, same "unsaved"
  // convention DynamicBillSummaryPanel's own SummaryKey column uses.
  // `persisted` (when set) is the actual raw `BoqSummaryLineItem` fetched from the
  // BOQ API for this extra item's own `fieldName` — shown in full via
  // `SummaryKeyRawFields` so the modal reflects every detail the API actually
  // has, not just the hand-picked subset of `live`'s fields. `live` (the
  // client-side node, always set) still drives the BCCalculation breakdown,
  // since that derivation needs the node's own `resourceCodes` array — a
  // persisted record only ever carries the combined `resourceCode` string.
  const [extraSummaryKeyRecord, setExtraSummaryKeyRecord] = useState<{
    live: DynamicBillSummaryLineItem;
    persisted: BoqSummaryLineItem | null;
  } | null>(null);

  // Ad-hoc "Add Item"/"Add Section" extra rows — gated by the "Configure BOQ" switch (next to the Re-calculate
  // button on /groundworks/review), independent of Dev Mode (useCanConfigureBoq(), hooks/useCanConfigureBoq.ts) — same
  // convention as the other groundworks Bill Summaries. Only wired into the
  // Collective view (see collectiveDisplayData below).
  const dynamicSummaryEnabled = useCanConfigureBoq();
  const geoWallsExtraItems = state.groundworks?.geoWallsExtraItems;
  const extraItemsPlotMatrixColumns = useMemo(
    () => generatePlotMatrixCategoryColumns(state.groundworks as any),
    [state.groundworks],
  );
  const [newlyAddedRowId, setNewlyAddedRowId] = useState<string | null>(null);
  useEffect(() => {
    if (!newlyAddedRowId) return;
    const timer = setTimeout(() => setNewlyAddedRowId(null), 2000);
    return () => clearTimeout(timer);
  }, [newlyAddedRowId]);

  const extraItemsApi = useBillSummaryExtraItems({
    extraItems: geoWallsExtraItems,
    onChange: (updated) =>
      updateField("groundworks", { ...state.groundworks, geoWallsExtraItems: updated }),
    itemLibraryData,
    plotMatrixColumns: extraItemsPlotMatrixColumns,
    onNodeAdded: (expandKey, nodeId) => {
      if (expandKey) {
        setExpandedRowKeys((prev) => (prev.includes(expandKey) ? prev : [...prev, expandKey]));
      }
      setNewlyAddedRowId(nodeId);
    },
  });

  // Dev-mode only: pulls the persisted `groundworks.summary.geoWalls` tree from the same
  // boq-versions API (`getBoqVersion`) that drives the rest of the review page, so the
  // SummaryKey column reflects what was actually saved rather than the live recomputation
  // below. Shape is category → spec → item, flattened here into two lookups: `bySpecId`
  // (spec.id → its node) and `byItemKey` (`${specId}::${itemId}` → the nested build-up item
  // node), since build-up item ids repeat across different wall specs.
  const { data: latestBoqVersionData } = useBoqLatestVersion(
    state.projectName || undefined,
    { enabled: showBillModeToggle && !!state.projectName, oppNo: state.opportunityNo },
  );

  const { bySpecId: apiGeoWallsBySpecId, byItemKey: apiGeoWallsByItemKey } = useMemo(() => {
    const bySpecId = new Map<string, BoqSummaryLineItem>();
    const byItemKey = new Map<string, BoqSummaryLineItem>();
    const topLevel = (latestBoqVersionData as unknown as { boq?: { billOfQuantities?: Array<{ groundworks?: { summary?: { geoWalls?: BoqSummaryLineItem[] } } }> } })
      ?.boq?.billOfQuantities?.[0]?.groundworks?.summary?.geoWalls;
    (topLevel || []).forEach((categoryNode) => {
      (categoryNode.children || []).forEach((specNode) => {
        if (specNode.fieldName) bySpecId.set(specNode.fieldName, specNode);
        (specNode.children || []).forEach((itemNode) => {
          if (specNode.fieldName && itemNode.fieldName) {
            byItemKey.set(`${specNode.fieldName}::${itemNode.fieldName}`, itemNode);
          }
        });
      });
    });
    return { bySpecId, byItemKey };
  }, [latestBoqVersionData]);

  // Ad-hoc extra items (Collective view only) persist as their own TOP-LEVEL
  // entries in `summary.geoWalls` (see flattenCutFillsExtraItemsToBoqLines /
  // boqPayloadBuilder.ts) — siblings of the category nodes `bySpecId`/
  // `byItemKey` above walk INTO, not something reachable via that 2-level-deep
  // walk. Indexed the same flat "top-level fieldName → node" way every other
  // Bill Summary's own apiXSummaryByFieldName map is built.
  const apiGeoWallsTopLevelByFieldName = useMemo(() => {
    const map = new Map<string, BoqSummaryLineItem>();
    const topLevel = (latestBoqVersionData as unknown as { boq?: { billOfQuantities?: Array<{ groundworks?: { summary?: { geoWalls?: BoqSummaryLineItem[] } } }> } })
      ?.boq?.billOfQuantities?.[0]?.groundworks?.summary?.geoWalls;
    (topLevel || []).forEach((node) => {
      if (node.fieldName) map.set(node.fieldName, node);
    });
    return map;
  }, [latestBoqVersionData]);

  // ── COLLECTIVE + INDIVIDUAL BILL (single shared computation) ─────────────────
  //
  // Both views share the exact same intermediate values, guaranteeing identical
  // grand totals. Individual: qty = takeoff metres, rate = net × baseRate (no OHP).
  const { collectiveProcessedData, individualProcessedData } = useMemo(() => {
    const empty = { collectiveProcessedData: [] as BillRow[], individualProcessedData: [] as BillRow[] };

    const tableData = state.groundworks?.geoToberWallTable || [];
    if (tableData.length === 0) return empty;

    const buildUpData = (state.groundworks?.geoToberWallBuildUpData || {}) as Record<string, any[]>;
    const ohpPercent = settings.groundworks.overheadAndProfit;
    const prelimsPercent = settings.groundworks.preliminaries;

    // Total metres per spec from take-off
    const wallTypeTotals: Record<string, number> = {};
    tableData.forEach((row: any) => {
      Object.entries(geoToberWallFieldToSpecId).forEach(([fieldName, specId]) => {
        wallTypeTotals[specId] = (wallTypeTotals[specId] || 0) + ((row[fieldName] as number) || 0);
      });
    });

    // Group active specs by category for collective view
    const categoryMap = new Map<string, typeof geoToberWallSpecifications>();
    geoToberWallSpecifications
      .filter((spec) => (wallTypeTotals[spec.id] || 0) > 0)
      .forEach((spec) => {
        if (!categoryMap.has(spec.category)) categoryMap.set(spec.category, []);
        categoryMap.get(spec.category)!.push(spec);
      });

    // Per-spec computed values — netPerM (no OHP) for individual, ratePerM (with OHP) for collective.
    // Sourced from `computeGeoToberWallSpecAmounts` (wallBuildUpCollectiveAmounts.ts)
    // — the single calculation for this, also used by Plot Matrix, so the two
    // can't drift apart the way they used to when each maintained its own
    // separate netPerM/rate derivation.
    type SpecItem = { id: string; description: string; netRate: number; dbRate: number; evaluateCode: string };
    const markupFactor = (1 + ohpPercent / 100) * (1 + prelimsPercent / 100);
    const persistedDimensionsBySpec: Record<string, WallBuildUpDimensionItem[]> = {};
    persistedBuildUp?.specifications?.forEach((s) => {
      persistedDimensionsBySpec[s.id] = s.items;
    });
    // Live build-up edits (from GeoToberWallSectionBuildUp, kept in tender
    // state as `geoToberWallBuildUpDimensions`) take precedence over the
    // persisted API snapshot — otherwise unsaved edits on the build-up page
    // never reach this summary until the next save + refetch.
    const liveDimensionsBySpec = ((state.groundworks as any)?.geoToberWallBuildUpDimensions) as
      Record<string, WallBuildUpDimensionItem[]> | undefined;
    const specAmounts = computeGeoToberWallSpecAmounts(
      tableData,
      buildUpData,
      persistedDimensionsBySpec,
      itemLibraryRateMap,
      markupFactor,
      liveDimensionsBySpec,
    );
    const specComputed: Record<string, { netPerM: number; ratePerM: number; wallQtyM: number; items: SpecItem[] }> = {};
    specAmounts.forEach((amt, specId) => {
      specComputed[specId] = {
        netPerM: amt.netPerM,
        ratePerM: amt.ratePerM,
        wallQtyM: amt.wallQtyM,
        // `dbRate` is the DB Rate column (raw per-unit item-library rate, unscaled
        // by `net`) — not returned by the shared function (which only tracks the
        // combined netRate), so re-derive it the same way for that column alone.
        items: amt.items.map((item) => ({
          ...item,
          dbRate: item.evaluateCode ? getRateFromLibrary(item.evaluateCode) : 0,
        })),
      };
    });

    // Build collective rows
    const collectiveRows: BillRow[] = [];
    let categoryIndex = 1;
    categoryMap.forEach((specs, categoryName) => {
      const children: BillRow[] = [];
      let sectionAmount = 0;
      let specIndex = 1;

      specs.forEach((spec) => {
        const { ratePerM, wallQtyM } = specComputed[spec.id] ?? { ratePerM: 0, wallQtyM: 0 };
        const roundedWallQtyM = Math.round(wallQtyM);
        const rowAmount = roundedWallQtyM * parseFloat(ratePerM.toFixed(2));
        sectionAmount += parseFloat(rowAmount.toFixed(2));
        children.push({
          key: spec.id,
          category: `${categoryIndex}.${specIndex}`,
          description: getTpcwLabel(spec.id, spec.name),
          quantity: String(roundedWallQtyM),
          unit: "m",
          rate: ratePerM.toFixed(2),
          amount: rowAmount.toFixed(2),
          rowType: "line-item",
          isDiameterHeader: false,
          dbRate: ratePerM.toFixed(4),
          apiSummaryItem: apiGeoWallsBySpecId.get(spec.id) || null,
          configViolation: configViolationsByField.get(specIdToField[spec.id]) ?? null,
        });
        specIndex++;
      });

      collectiveRows.push({
        key: `cat-${categoryName}`,
        category: `${categoryIndex}.0`,
        description: categoryName.toUpperCase(),
        quantity: "",
        unit: "",
        rate: "",
        amount: "",
        rowType: "sub-header",
        isDiameterHeader: true,
        children,
        sectionAmount,
        categoryName,
      });
      categoryIndex++;
    });

    // Build individual rows — spec header (collapsible) with build-up items as children (as-is from build-up)
    let indIndex = 1;
    const individualRows: BillRow[] = geoToberWallSpecifications
      .filter((spec) => (wallTypeTotals[spec.id] || 0) > 0)
      .map((spec) => {
        const { wallQtyM, items } = specComputed[spec.id] ?? { wallQtyM: 0, items: [] };
        const roundedQty = Math.round(wallQtyM);
        const idx = indIndex++;

        const children: BillRow[] = items.map((item, ci) => {
          const rateStr = item.netRate.toFixed(2);
          const amount = Math.abs(roundedQty * parseFloat(rateStr)).toFixed(2);
          return {
            key: `ind-${spec.id}-item-${ci}`,
            category: `${idx}.${ci + 1}`,
            description: item.description,
            quantity: String(roundedQty),
            unit: 'm',
            rate: rateStr,
            amount,
            rowType: 'line-item' as const,
            isDiameterHeader: false,
            evaluateCode: item.evaluateCode,
            dbRate: item.dbRate.toFixed(4),
            apiSummaryItem: apiGeoWallsByItemKey.get(`${spec.id}::${item.id}`) || null,
          };
        });

        const specTotal = children.reduce((s, c) => s + parseFloat(c.amount || '0'), 0);

        return {
          key: `ind-${spec.id}`,
          category: String(idx),
          description: getTpcwLabel(spec.id, spec.name),
          quantity: '',
          unit: '',
          rate: '',
          amount: specTotal.toFixed(2),
          rowType: 'sub-header' as const,
          isDiameterHeader: false,
          sectionAmount: specTotal,
          children: children.length > 0 ? children : undefined,
        };
      });

    return { collectiveProcessedData: collectiveRows, individualProcessedData: individualRows };
  }, [state.groundworks, settings.groundworks.overheadAndProfit, settings.groundworks.preliminaries, getRateFromLibrary, tpcwColumnHeaders, tpcwDimensionValues, persistedBuildUp, persistedSpecNameMap, apiGeoWallsBySpecId, apiGeoWallsByItemKey, configViolationsByField]);

  // Graft ad-hoc extra rows on top of the Collective view's otherwise-immutable
  // computed tree — nested additions merge onto the matching existing category
  // row (by `key`), and root additions become new top-level sections
  // continuing the same whole-number numbering the category rows above use.
  // Same convention GroundworksCutFillBillSummary.tsx / FoundationsBillSummary.tsx
  // follow. Individual view is untouched.
  const collectiveDisplayData: any[] = useMemo(() => {
    const extraBuckets = geoWallsExtraItems ?? {};
    // Same OH&P/Preliminaries markup every fixed row in this table already
    // gets (see collectiveProcessedData's own ohpPercent/prelimsPercent
    // above) — applied here too so extra rows' Rate/Amount match.
    const markupFactor =
      (1 + settings.groundworks.overheadAndProfit / 100) * (1 + settings.groundworks.preliminaries / 100);
    // Buckets whose attach-point row is actually present in this render's
    // computed tree — anything left over (see orphanedExtraRows below) had
    // its row disappear (e.g. the Geowall & Tobermore Walls take-off table
    // was emptied, dropping every row it was nested under) and must not be
    // silently lost.
    const consumedAttachKeys = new Set<string>();
    const injectExtraRows = (rows: any[]): any[] =>
      rows.map((row) => {
        const bucket = extraBuckets[row.key];
        const existingChildren: any[] = row.children ?? [];
        const ownChildren = row.children ? injectExtraRows(row.children) : undefined;
        if (!bucket || bucket.length === 0) {
          return ownChildren ? { ...row, children: ownChildren } : row;
        }
        consumedAttachKeys.add(row.key);
        const extraRows = buildNestedExtraRows(bucket, row.key, row.category, existingChildren.length, markupFactor);
        const mergedChildren = [...(ownChildren ?? []), ...extraRows];
        return {
          ...row,
          children: mergedChildren,
          ...(row.sectionAmount !== undefined && {
            sectionAmount: mergedChildren.reduce((sum, c) => sum + (parseFloat(c.amount || "0") || 0), 0),
          }),
        };
      });
    const injectedResult = injectExtraRows(collectiveProcessedData);
    let nextRootNumber = collectiveProcessedData.length + 1;
    const rootExtraRows = buildRootExtraRows(extraBuckets["__root__"] ?? [], "__root__", nextRootNumber, markupFactor);
    nextRootNumber += rootExtraRows.length;
    // Orphaned nested buckets (attach-point row no longer exists) wrap under
    // a single synthetic "Additional Items" header instead of landing as
    // bare unlabelled rows — not a real node itself (Edit/Delete never
    // targets it, only its real children match a node id), just keyed by
    // the REAL attachKey so those children's Edit/Delete/Qty edits still
    // address the correct bucket in state.
    const orphanedExtraRows = Object.keys(extraBuckets)
      .filter((key) => key !== "__root__" && !consumedAttachKeys.has(key) && extraBuckets[key].length > 0)
      .map((key) => {
        const n = nextRootNumber++;
        const children = buildNestedExtraRows(extraBuckets[key], key, `${n}.0`, 0, markupFactor);
        // Category rows are keyed `cat-${categoryName}` — recover the
        // original category name straight from the key so the orphaned
        // section keeps its real title instead of a generic placeholder.
        const originalLabel = key.startsWith("cat-") ? key.slice("cat-".length).toUpperCase() : "Additional Items";
        return {
          key: `orphaned-${key}`,
          category: `${n}.0`,
          description: originalLabel,
          rowType: "main-header",
          isExtra: true,
          attachKey: key,
          nodeType: "section",
          quantity: "",
          unit: "",
          rate: "",
          amount: "",
          dbRate: "",
          sectionAmount: children.reduce((sum, c) => sum + (parseFloat(c.amount || "0") || 0), 0),
          children,
        };
      });
    return [...injectedResult, ...rootExtraRows, ...orphanedExtraRows];
  }, [collectiveProcessedData, geoWallsExtraItems]);

  // Depth of every row (real category rows AND grafted extra rows alike) in
  // the merged `collectiveDisplayData` tree — gates a section row's "Add
  // Section" Actions icon at MAX_SECTION_NESTING_DEPTH, counting
  // pre-existing real category nesting as well as already-added extra
  // sections.
  const rowDepthMap = useMemo(() => buildSectionDepthMap(collectiveDisplayData), [collectiveDisplayData]);

  // ── SHARED UI ────────────────────────────────────────────────────────────────

  if (isLoadingItemLibrary) {
    return (
      <div style={{ padding: "16px", textAlign: "center" }}>
        <Spin size="large" />
        <div style={{ marginTop: "16px" }}>
          <Text className="text-gray-500">Loading item library rates...</Text>
        </div>
      </div>
    );
  }

  const hasData =
    state.groundworks?.geoToberWallTable &&
    state.groundworks.geoToberWallTable.length > 0;
  // An empty Geowall & Tobermore Walls take-off table alone must NOT hide
  // already-saved ad-hoc extra items — see
  // GroundworksCutFillBillSummary.tsx's identical fix.
  const hasExtraItems = Object.values(geoWallsExtraItems ?? {}).some((bucket) => bucket.length > 0);

  if (!hasData && !hasExtraItems) {
    return (
      <div style={{ padding: "16px" }}>
        <Text className="text-gray-500">
          No Geowall &amp; Tobermore Walls data available. Please add plots in
          the Geowall &amp; Tobermore Walls Takeoff section first.
        </Text>
      </div>
    );
  }

  const sharedColumns = (mode: BillMode) => [
    {
      title: "Item",
      dataIndex: "category",
      key: "category",
      width: 110,
      align: "center" as const,
      render: (value: string, record: BillRow) => {
        if (isHeaderRowType(record.rowType) || record.rowType === "description-header") {
          return <Text strong>{value}</Text>;
        }
        return value;
      },
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: 350,
      render: (value: string, record: BillRow) => {
        if (isHeaderRowType(record.rowType) || record.rowType === "description-header") return <Text strong className="text-indigo-600">{value}</Text>;
        if (record.rowType === "description") return <Text italic className="text-gray-600">{value}</Text>;
        return (
          <div>
            <Text>{value}</Text>
            {record.configViolation && (
              <Tooltip title="Configure Headers required — click for details">
                <WarningOutlined
                  style={{ color: '#faad14', marginLeft: 6, cursor: 'pointer' }}
                  onClick={() => setConfigViolationModalRecord(record.configViolation!)}
                />
              </Tooltip>
            )}
            {showBillModeToggle && (record.evaluateCode || record.resourceCode || (record.resourceCodes && record.resourceCodes.length > 0)) && (
              <div style={{ fontSize: 10, color: '#999', fontWeight: 500, marginTop: 2 }}>
                {record.evaluateCode || record.resourceCode || record.resourceCodes!.map((c, i) => (i === 0 ? c.resourceCode : `${c.operator} ${c.resourceCode}`)).join(' ')}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: "Qty",
      dataIndex: "quantity",
      key: "quantity",
      width: 110,
      align: "right" as const,
      render: (value: string, record: BillRow) => {
        if (isHeaderRowType(record.rowType) || record.rowType === "description-header" || record.rowType === "description") return "";
        // Ad-hoc extra items are directly editable in-cell (Collective view
        // only, record.isExtra never true in Individual), same as
        // GroundworksCutFillBillSummary.tsx's own Qty column — existing
        // computed rows stay read-only. Text amount type has no Qty at all.
        if (mode === "collective" && record.isExtra && record.nodeType === 'item' && record.amountType !== 'text' && dynamicSummaryEnabled && !state.isViewMode) {
          return (
            <NumberInput
              value={parseFloat(value) || 0}
              onChange={(qty) => extraItemsApi.updateNode(record.attachKey!, record.key, { qty })}
              size="small"
              decimalPlaces={2}
              autoFocus={record.key === newlyAddedRowId}
            />
          );
        }
        if (!value || value === "" || value === "0" || value === "0.00") return "";
        const num = parseFloat(value);
        if (isNaN(num)) return "";
        return (
          <div style={{ textAlign: "right" }}>
            {Math.round(num).toLocaleString("en-GB")}
          </div>
        );
      },
    },
    {
      title: "Unit",
      dataIndex: "unit",
      key: "unit",
      width: 110,
      align: "center" as const,
      render: (value: string, record: BillRow) => {
        if (isHeaderRowType(record.rowType) || record.rowType === "description-header" || record.rowType === "description") return "";
        // Same in-cell Select every extra-items Unit column uses.
        if (mode === "collective" && record.isExtra && record.nodeType === 'item' && record.amountType !== 'text' && dynamicSummaryEnabled && !state.isViewMode) {
          return (
            <Select
              value={value || undefined}
              onChange={(unit) => extraItemsApi.updateNode(record.attachKey!, record.key, { unit })}
              options={extraItemsApi.unitOptions.map((u) => ({ value: u, label: u }))}
              placeholder="Unit"
              size="small"
              showSearch
              allowClear
              style={{ width: "100%" }}
            />
          );
        }
        return formatUnit(value);
      },
    },
    {
      title: "Rate",
      dataIndex: "rate",
      key: "rate",
      width: 110,
      align: "right" as const,
      render: (value: string, record: BillRow) => {
        if (isHeaderRowType(record.rowType) || record.rowType === "description-header" || record.rowType === "description") return "";
        if (!record.evaluateCode && !record.isExtra) return "";
        if (!value || value === "" || value === "0.00") return "";
        const num = parseFloat(value);
        return isNaN(num) ? "" : <div style={{ textAlign: "right" }}>£{num.toFixed(2)}</div>;
      },
    },
    ...(showBillModeToggle && mode === "individual" ? [{
      title: 'DB Rate',
      dataIndex: 'dbRate',
      key: 'dbRate',
      width: 100,
      align: 'right' as const,
      render: (value: string, record: BillRow) => {
        if (record.rowType === 'sub-header' || record.rowType === 'description-header' || record.rowType === 'description') return '';
        if (!value || value === '') return '';
        const numValue = parseFloat(value);
        if (isNaN(numValue) || numValue === 0) return '';
        return <div style={{ textAlign: 'right', color: '#9ca3af', fontSize: '12px' }}>£{numValue.toFixed(4)}</div>;
      },
    }] : []),
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      width: 120,
      align: "right" as const,
      render: (value: string, record: BillRow) => {
        if (record.rowType === "description") return "";
        if (isHeaderRowType(record.rowType) || record.rowType === "description-header") {
          if (mode === "individual") {
            if (!value || value === "0.00") return "";
            return (
              <div style={{ textAlign: "right" }}>
                <span className="font-semibold">
                  £{Math.abs(parseFloat(value)).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
            );
          }
          // collective: show section total only when collapsed
          if (record.children && record.children.length > 0) {
            const isExpanded = expandedRowKeys.includes(record.key);
            if (!isExpanded && (record.sectionAmount || 0) > 0) {
              return (
                <div style={{ textAlign: "right" }}>
                  <span className="font-semibold">
                    £{(record.sectionAmount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
              );
            }
          }
          return "";
        }
        // Text amount type (extra items only) — a free-typed value like
        // "Included"/"Rate Only" instead of a computed £ amount, same
        // convention DynamicBillSummaryPanel's own Amount column uses.
        if (record.amountType === 'text') {
          return <div style={{ textAlign: 'right' }}><Text>{record.amountText}</Text></div>;
        }
        if (!value || value === "0.00") return "";
        return (
          <div style={{ textAlign: "right" }}>
            <Text strong>
              £{Math.abs(parseFloat(value)).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </Text>
          </div>
        );
      },
    },
    ...(showBillModeToggle ? [{
      title: 'SummaryKey',
      dataIndex: 'apiSummaryItem',
      key: 'summaryKey',
      width: 150,
      align: 'right' as const,
      render: (value: BoqSummaryLineItem | null | undefined, record: BillRow) => {
        if (isHeaderRowType(record.rowType) || record.rowType === 'description-header' || record.rowType === 'description') return '';
        if (mode === "collective" && record.isExtra) {
          const node = findExtraNode(geoWallsExtraItems?.[record.attachKey!] ?? [], record.key) as DynamicBillSummaryItemNode | null;
          if (!node) return '';
          const liveAmount = parseFloat(String(record.amount ?? '0')) || 0;
          const lineItem: DynamicBillSummaryLineItem = {
            key: node.id,
            description: node.description,
            quantity: node.qty,
            unit: node.unit,
            rate: node.rate,
            amount: liveAmount,
            amountType: node.amountType,
            amountText: node.amountText,
            resourceCode: node.resourceCode,
            resourceCodes: node.resourceCodes,
          };
          const bc = computeDynamicBcCalculation(lineItem, (code) => itemLibraryRateMap.get(code) ?? 0);
          // Once saved, this extra item persists into the real BOQ document's
          // `summary.geoWalls` array as its own top-level entry (see
          // flattenCutFillsExtraItemsToBoqLines, boqPayloadBuilder.ts) — same
          // persisted-vs-live comparison every other row's SummaryKey shows.
          // Before the first save, there's nothing to compare yet, so this
          // falls back to a live-derived "unsaved" value instead — same
          // convention DynamicBillSummaryPanel's own SummaryKey column uses.
          const persisted = apiGeoWallsTopLevelByFieldName.get(record.key);
          const persistedAmount = persisted?.amount ?? liveAmount;
          const mismatch = !!persisted && Math.abs(liveAmount - persistedAmount) > 0.01;
          return (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <Text style={{ fontSize: 12, color: mismatch ? '#dc2626' : persisted ? '#6b7280' : '#f59e0b' }}>
                  £{persistedAmount.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Text>
                <InfoCircleOutlined
                  style={{ cursor: 'pointer', color: '#3b82f6' }}
                  onClick={() => setExtraSummaryKeyRecord({ live: lineItem, persisted: persisted ?? null })}
                />
              </div>
              {bc && (
                <Text style={{ fontSize: 10, color: mismatch ? '#dc2626' : '#3b82f6' }}>
                  BC: £{bc.amount.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Text>
              )}
              {!persisted && <Text style={{ fontSize: 10, color: '#f59e0b' }}>unsaved</Text>}
            </div>
          );
        }
        if (!value) return '';
        const bcAmount = computeBcCalculation(value, (code) => itemLibraryRateMap.get(code) ?? 0).amount;
        const liveAmount = parseFloat(String(record.amount ?? '0')) || 0;
        const mismatch = Math.abs(liveAmount - bcAmount) > 0.01;
        return (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <Text style={{ fontSize: 12, color: mismatch ? '#dc2626' : '#6b7280' }}>
                £{(value.amount || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </Text>
              <InfoCircleOutlined
                style={{ cursor: 'pointer', color: '#3b82f6' }}
                onClick={() => setSummaryKeyModalRecord(value)}
              />
            </div>
            <Text style={{ fontSize: 10, color: mismatch ? '#dc2626' : '#3b82f6' }}>
              BC: £{bcAmount.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </Text>
          </div>
        );
      },
    }] : []),
    ...(mode === "collective" && dynamicSummaryEnabled && !state.isViewMode ? [{
      title: 'Actions',
      key: 'extraActions',
      width: 150,
      align: 'center' as const,
      render: (_: unknown, record: BillRow) => {
        if (record.rowType === 'description') return '';
        if (record.isExtra) {
          const node = findExtraNode(geoWallsExtraItems?.[record.attachKey!] ?? [], record.key);
          if (!node) return '';
          if (record.nodeType === 'section') {
            return (
              <Space size={4}>
                <Tooltip title={(rowDepthMap.get(record.key) ?? 0) >= MAX_SECTION_NESTING_DEPTH ? MAX_SECTION_NESTING_DEPTH_MESSAGE : undefined}>
                  <Button
                    size="small"
                    icon={<FolderAddOutlined />}
                    title="Add Section"
                    disabled={(rowDepthMap.get(record.key) ?? 0) >= MAX_SECTION_NESTING_DEPTH}
                    onClick={() => extraItemsApi.openAddSection(record.attachKey!, record.key)}
                  />
                </Tooltip>
                <Button size="small" icon={<PlusOutlined />} title="Add Item" onClick={() => extraItemsApi.openAddItem(record.attachKey!, record.key)} />
                <Button size="small" icon={<EditOutlined />} title="Edit Section" onClick={() => extraItemsApi.openEditSection(record.attachKey!, node as DynamicBillSummarySectionNode)} />
                <Button size="small" icon={<DeleteOutlined />} danger title="Delete" onClick={() => extraItemsApi.requestDelete(record.attachKey!, record.key)} />
              </Space>
            );
          }
          return (
            <Space size={4}>
              <Button size="small" icon={<EditOutlined />} title="Edit Item" onClick={() => extraItemsApi.openEditItem(record.attachKey!, node as DynamicBillSummaryItemNode)} />
              <Button size="small" icon={<DeleteOutlined />} danger title="Delete" onClick={() => extraItemsApi.requestDelete(record.attachKey!, record.key)} />
            </Space>
          );
        }
        // Existing computed row — immutable, but a section row can host
        // grafted extra rows. A plain computed line-item gets no Actions at
        // all — only sections are attachment points.
        if (!isHeaderRowType(record.rowType) && record.rowType !== 'description-header') {
          return '';
        }
        {
          const atMaxDepth = (rowDepthMap.get(record.key) ?? 0) >= MAX_SECTION_NESTING_DEPTH;
          return (
            <Space size={4}>
              <Tooltip title={atMaxDepth ? MAX_SECTION_NESTING_DEPTH_MESSAGE : undefined}>
                <Button
                  size="small"
                  icon={<FolderAddOutlined />}
                  title="Add Section"
                  disabled={atMaxDepth}
                  onClick={() => extraItemsApi.openAddSection(record.key, null)}
                />
              </Tooltip>
              <Button size="small" icon={<PlusOutlined />} title="Add Item" onClick={() => extraItemsApi.openAddItem(record.key, null)} />
            </Space>
          );
        }
      },
    }] : []),
  ];

  return (
    <div style={{ padding: "16px" }}>
      {/* Header + mode selector */}
      <div
        style={{
          marginBottom: "16px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Title
          level={5}
          style={{ margin: 0, fontWeight: 600, fontSize: "16px", color: "#111827" }}
        >
          Geowall &amp; Tobermore Walls Bill Summary
        </Title>
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          {billMode === "collective" && dynamicSummaryEnabled && !state.isViewMode && (
            <Space size={4}>
              <Button size="small" icon={<FolderAddOutlined />} onClick={() => extraItemsApi.openAddSection("__root__", null)}>
                Add Section
              </Button>
              {/* Root-level "Add Item" button hidden per request (2026-09-01) — row-level Add Item icons remain.
              <Button size="small" icon={<PlusOutlined />} onClick={() => extraItemsApi.openAddItem("__root__", null)}>
                Add Item
              </Button>
              */}
            </Space>
          )}
          <ExpandCollapseAllButton
            expanded={expandedRowKeys.length > 0}
            onToggle={() => {
              if (expandedRowKeys.length > 0) {
                setExpandedRowKeys([]);
              } else {
                const allKeys: React.Key[] = [];
                const collect = (rows: any[]) => {
                  rows.forEach((row) => {
                    if (row.children?.length) {
                      allKeys.push(row.key);
                      collect(row.children);
                    }
                  });
                };
                collect(billMode === "collective" ? collectiveDisplayData : individualProcessedData);
                setExpandedRowKeys(allKeys);
              }
            }}
          />
          {showBillModeToggle && (
            <Segmented
              value={billMode}
              onChange={(val) => { setBillMode(val as BillMode); setExpandedRowKeys([]); }}
              options={[
                { label: "Collective", value: "collective" },
                { label: "Individual", value: "individual" },
              ]}
            />
          )}
        </div>
      </div>

      <style>
        {`
          .geo-tober-wall-summary-table .ant-table {
            border-radius: 0 !important;
          }
          .geo-tober-wall-summary-table .ant-table-container {
            border-radius: 0 !important;
          }
          .geo-tober-wall-summary-table .ant-table-thead > tr > th {
            background-color: ${COLORS.tableHeaderBg} !important;
          }
        `}
      </style>

      {/* ── COLLECTIVE BILL ── */}
      {billMode === "collective" && (
        <Table
          className="geo-tober-wall-summary-table"
          dataSource={collectiveDisplayData}
          expandable={{
            childrenColumnName: "children",
            defaultExpandAllRows: false,
            expandedRowKeys,
            onExpand: (expanded, record) => {
              if (expanded) {
                setExpandedRowKeys([...expandedRowKeys, record.key]);
              } else {
                setExpandedRowKeys(expandedRowKeys.filter((k) => k !== record.key));
              }
            },
            rowExpandable: (record) => !!(record.children && record.children.length > 0),
          }}
          columns={sharedColumns("collective")}
          pagination={false}
          size="middle"
          bordered
          rowClassName={(record: BillRow) => {
            const classes: string[] = [];
            if (isHeaderRowType(record.rowType)) classes.push('bg-indigo-50', 'font-semibold');
            else if (record.rowType === "description-header") classes.push('bg-indigo-50', 'font-semibold');
            else if (record.rowType === "description") classes.push('bg-gray-50');
            if (record.key === newlyAddedRowId) classes.push('geo-tober-wall-summary-row-added');
            return classes.join(' ');
          }}
          summary={(pageData) => {
            const flattenAll = (items: readonly any[]): any[] => {
              let result: any[] = [];
              items.forEach((item) => {
                if (item.rowType === "line-item") result.push(item);
                if (item.children?.length > 0) result.push(...flattenAll(item.children));
              });
              return result;
            };
            const flat = flattenAll(pageData);
            const totalAmt = flat.reduce((sum, item) => sum + parseFloat(item.amount || "0"), 0);
            return (
              <Table.Summary.Row style={{ backgroundColor: "#fafafa", fontWeight: 700 }}>
                <Table.Summary.Cell index={0} colSpan={2}>
                  <Text strong style={{ fontWeight: 700 }}>TOTAL GEOWALL &amp; TOBERMORE WALLS</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2} />
                <Table.Summary.Cell index={3} />
                <Table.Summary.Cell index={4} />
                <Table.Summary.Cell index={5} align="right">
                  <span style={{ display: "block", textAlign: "right", fontWeight: 700 }}>
                    £{totalAmt.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </Table.Summary.Cell>
                {showBillModeToggle && <Table.Summary.Cell index={6} />}
                {dynamicSummaryEnabled && !state.isViewMode && <Table.Summary.Cell index={7} />}
              </Table.Summary.Row>
            );
          }}
          locale={{ emptyText: "No wall data found. Add values in the Geowall & Tobermore Takeoff tab first." }}
        />
      )}

      <style>{`
        /* Newly-added extra Section/Item row (see newlyAddedRowId) — a brief
           highlight fade so the row is easy to spot the moment its parent
           auto-expands to reveal it. */
        @keyframes geo-tober-wall-summary-row-added-fade {
          0% { background-color: #fef3c7; }
          100% { background-color: transparent; }
        }
        .geo-tober-wall-summary-row-added > td {
          animation: geo-tober-wall-summary-row-added-fade 1.8s ease-out;
        }
      `}</style>

      {/* ── INDIVIDUAL BILL ── */}
      {billMode === "individual" && (
        <Table
          className="geo-tober-wall-summary-table"
          dataSource={individualProcessedData}
          expandable={{
            childrenColumnName: "children",
            defaultExpandAllRows: false,
            expandedRowKeys,
            onExpand: (expanded, record) => {
              if (expanded) {
                setExpandedRowKeys([...expandedRowKeys, record.key]);
              } else {
                setExpandedRowKeys(expandedRowKeys.filter((k) => k !== record.key));
              }
            },
            rowExpandable: (record) => !!(record.children && record.children.length > 0),
          }}
          columns={sharedColumns("individual")}
          pagination={false}
          size="middle"
          bordered
          rowClassName={(record: BillRow) => {
            if (record.rowType === "sub-header") return "bg-indigo-50 font-semibold";
            if (record.rowType === "description-header") return "bg-indigo-50 font-semibold";
            if (record.rowType === "description") return "bg-gray-50";
            return "";
          }}
          summary={(pageData) => {
            const flattenAll = (items: readonly any[]): any[] => {
              let result: any[] = [];
              items.forEach((item) => {
                if (item.children?.length > 0) result.push(...flattenAll(item.children));
                else if (item.rowType === 'line-item') result.push(item);
              });
              return result;
            };
            const totalAmt = flattenAll(pageData).reduce((sum, item) => sum + parseFloat(item.amount || "0"), 0);
            return (
              <Table.Summary.Row style={{ backgroundColor: "#fafafa", fontWeight: 700 }}>
                <Table.Summary.Cell index={0} colSpan={2}>
                  <Text strong style={{ fontWeight: 700 }}>TOTAL GEOWALL &amp; TOBERMORE WALLS</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2} />
                <Table.Summary.Cell index={3} />
                <Table.Summary.Cell index={4} />
                {showBillModeToggle && <Table.Summary.Cell index={5} />}
                <Table.Summary.Cell index={showBillModeToggle ? 6 : 5} align="right">
                  <span style={{ display: "block", textAlign: "right", fontWeight: 700 }}>
                    £{totalAmt.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </Table.Summary.Cell>
                {showBillModeToggle && <Table.Summary.Cell index={7} />}
              </Table.Summary.Row>
            );
          }}
          locale={{ emptyText: "No quantities calculated. Add Geowall & Tobermore data in the Takeoff tabs first." }}
        />
      )}

      <Modal
        title="Configure Headers required"
        open={!!configViolationModalRecord}
        onCancel={() => setConfigViolationModalRecord(null)}
        footer={
          <Button type="primary" onClick={() => setConfigViolationModalRecord(null)}>
            OK
          </Button>
        }
      >
        {configViolationModalRecord && (
          <div>
            <Text strong>{configViolationModalRecord.section}</Text>
            <div style={{ marginTop: 8 }}>
              <Text>{configViolationModalRecord.message}</Text>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        title="SummaryKey — Amount Calculation"
        open={!!summaryKeyModalRecord}
        onCancel={() => setSummaryKeyModalRecord(null)}
        footer={null}
      >
        {summaryKeyModalRecord && (
          <BcCalculationDetails
            record={summaryKeyModalRecord}
            getDbRate={(code) => itemLibraryRateMap.get(code) ?? 0}
          />
        )}
      </Modal>

      <Modal
        title={extraSummaryKeyRecord?.persisted ? "SummaryKey — Amount Calculation" : "SummaryKey — Live Value (not yet in BOQ export)"}
        open={!!extraSummaryKeyRecord}
        onCancel={() => setExtraSummaryKeyRecord(null)}
        footer={null}
      >
        {extraSummaryKeyRecord && (() => {
          const { live, persisted } = extraSummaryKeyRecord;
          const bc = computeDynamicBcCalculation(live, (code) => itemLibraryRateMap.get(code) ?? 0);
          return (
            <>
              <SummaryKeyRawFields record={(persisted ?? live) as unknown as Record<string, unknown>} />
              {bc && (
                <div style={{ marginTop: 8, padding: 8, background: '#eff6ff', borderRadius: 4 }}>
                  <Text strong>BCCalculation (backend dbRate-only derivation):</Text>
                  <Descriptions bordered size="small" column={1} style={{ marginTop: 8 }}>
                    <Descriptions.Item label="Computed Rate (rounded 2dp)">£{bc.rate.toFixed(2)}</Descriptions.Item>
                    <Descriptions.Item label="Quantity (rounded)">
                      {bc.quantity.toLocaleString('en-GB')} {formatUnit(live.unit)}
                    </Descriptions.Item>
                    <Descriptions.Item label="Amount (rounded 2dp)">
                      £{bc.amount.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </Descriptions.Item>
                  </Descriptions>
                  <div style={{ marginTop: 8 }}>
                    {bc.steps.map((step, idx) => (
                      <div key={idx}>
                        <Text style={{ fontSize: 12, color: '#374151' }}>{step}</Text>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          );
        })()}
      </Modal>

      {extraItemsApi.modals}
    </div>
  );
};

export default GEOToberWallBillSummary;
